import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { HomePageData, HomePageVariables } from 'src/modules/home/HomePageMapping.ts';
import { DocumentNode } from 'graphql/language/ast';
import { Filter, FilterConfig } from 'src/utils/composeFilter.ts';
import { ApolloClient } from '@apollo/client/core';
import { getFilterComposed } from 'src/modules/common/hooks/getFilterComposed.ts';
import { AttributeValueWithCount, LocalesEnum, RegionsEnum } from 'src/generated/operations.ts';
import { HomeState } from 'src/modules/home/types.ts';
import { MandatoryFilterInfo } from 'src/types/MandatoryFilterInfo.ts';
import { useSearchParams } from 'react-router-dom';
import { MandatoryFilter, OperationMode } from 'src/modules/common/constants.ts';
import { isPressureDependentSelected } from 'src/modules/home/isPressureDependentSelected.ts';

export function isItAStableFilter({
    data,
    filterConfig,
    filter,
}: {
    filterConfig: FilterConfig;
    filter: Filter;
    currentRegion: RegionsEnum;
    data?: HomePageData;
}) {
    if (!data) {
        return true;
    }
    for (const item of filterConfig) {
        const [name] = item;
        if (name === OperationMode && !isPressureDependentSelected(filter)) {
            continue;
        }
        const valuesAvailable = (data[name as keyof HomePageData]?.values ?? [])
            .map((v) => v?.value);
        const countAvailable = valuesAvailable.length;
        if (countAvailable !== 1) {
            continue;
        }
        const [value] = valuesAvailable;
        const isSelected = (filter[name] || []).includes(value);
        if (isSelected) {
            continue;
        }
        return false;
    }
    return true;
}

export async function getData({
    client,
    query,
    filter,
    filterFamily,
    filterConfig,
    mandatoryFilterConfig,
    currentLocale,
    currentRegion,
}: {
    client: ApolloClient<unknown>;
    query: DocumentNode;
    filterConfig: FilterConfig;
    filter: Filter;
    filterFamily: Filter;
    mandatoryFilterConfig: MandatoryFilterInfo;
    currentLocale: LocalesEnum;
    currentRegion: RegionsEnum;
}): Promise<undefined | { data: HomePageData; filter: Filter }> {
    const [filterMain, filterRaw] = getFilterComposed([
        ...filterConfig,
    ], filter, mandatoryFilterConfig, currentLocale);
    const [filterForFamily] = getFilterComposed([
        ...filterConfig,
    ], filterFamily, mandatoryFilterConfig, currentLocale);

    const { data, error } = await client.query<HomePageData, HomePageVariables>({
        query,
        variables: {
            filterForFamily,
            filter: filterMain,
            filterRaw,
            locale: currentLocale,
        },
    });

    if (error) {
        throw error;
    }

    if (!data) {
        return Promise.resolve(undefined);
    }

    const isStable = isItAStableFilter({
        data,
        filterConfig,
        filter,
        currentRegion,
    });

    if (isStable) {
        return {
            data,
            filter,
        };
    }

    const currentFilter = { ...filter };
    filterConfig.forEach((item) => {
        const [name] = item;
        if (name === OperationMode && !isPressureDependentSelected(currentFilter)) {
            return;
        }
        const valuesAvailable = (data[name as keyof HomePageData]?.values ?? [])
            .map((v: AttributeValueWithCount) => v?.value);
        const countAvailable = valuesAvailable.length;
        if (countAvailable !== 1) {
            return;
        }
        const currentValue = (currentFilter[name] || []);
        const isSelected = currentValue.filter((v) => valuesAvailable.includes(v)).length > 0;
        if (isSelected) {
            return;
        }
        currentFilter[name] = [...currentValue, ...valuesAvailable];
    });

    // for checkboxes (valve family short names) we must have additional filtering
    // to consider only available options from other filters
    // example:
    // user choose BFV
    // pressure independent got auto-selected
    // application got auto-selected
    // pattern have 6-way disabled - so we must add 2-way and 3-way options as additional filter
    // to get correct list of families (exclude 6-way families from the list)
    const currentFilterForFamily = { ...filterFamily };

    filterConfig.forEach((item) => {
        const [name] = item;
        if (name === MandatoryFilter.ValveType) {
            return;
        }
        if (name === OperationMode && !isPressureDependentSelected(currentFilterForFamily)) {
            return;
        }
        const valuesAvailable = (data[name as keyof HomePageData]?.values ?? [])
            .map((v: AttributeValueWithCount) => v?.value);
        const currentValue = (currentFilterForFamily[name] || []);
        const isSelected = currentValue.filter((v) => valuesAvailable.includes(v)).length > 0;
        if (isSelected) {
            return;
        }
        currentFilterForFamily[name] = [...currentValue, ...valuesAvailable];
    });

    return getData({
        client,
        query,
        filterConfig,
        currentLocale,
        currentRegion,
        mandatoryFilterConfig,
        filter: currentFilter,
        filterFamily: currentFilterForFamily,
    });
}

export function useGetHomeData({
    query,
    filter,
    filterConfig,
    mandatoryFilterConfig,
    currentLocale,
    currentRegion,
}: {
    query: DocumentNode;
    filterConfig: FilterConfig;
    filter: Filter;
    mandatoryFilterConfig: MandatoryFilterInfo;
    currentLocale: LocalesEnum;
    currentRegion: RegionsEnum;
}) {
    const client = useApolloClient();
    const [, setSearchParams] = useSearchParams();
    const [state, setData] = useState<HomeState>({
        data: undefined,
        isLoading: false,
        filter: {},
    });
    useEffect(() => {
        setData((prev) => ({ ...prev, isLoading: true, error: undefined }));
        getData({
            client,
            query,
            filter,
            filterFamily: filter,
            filterConfig,
            mandatoryFilterConfig,
            currentLocale,
            currentRegion,
        })
            .then((result) => {
                const data = result ? result.data : undefined;
                setData({ data, isLoading: false, error: undefined, filter: result ? result.filter : filter });
            })
            .catch((error) => setData({ error, data: undefined, isLoading: false, filter }));
    }, [
        setSearchParams,
        client,
        query,
        filterConfig,
        filter,
        mandatoryFilterConfig,
        currentLocale,
        currentRegion,
    ]);
    return state;
}
